/**
 * Expiring Parts Widget — issue #209
 *
 * Lists stock_levels rows whose expiry_date is within `days` of today.
 * Already-expired rows are shown first so they can be cleared. Color
 * coding mirrors the LowStockAlertWidget convention: red (error) for
 * critical (expired or < 7 days), orange (warning) otherwise.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { ExpiringPart } from '@/hooks/useInventory';
import { dateFnsLocale } from '@/utils/locale';

interface ExpiringPartsWidgetProps {
  items: ExpiringPart[] | undefined;
  isLoading?: boolean;
  onRefresh?: () => void;
  maxItemsToShow?: number;
}

type Severity = 'critical' | 'warning';

interface DecoratedItem extends ExpiringPart {
  daysRemaining: number;
  severity: Severity;
}

const CRITICAL_THRESHOLD_DAYS = 7;

function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function ExpiringPartsWidget({
  items,
  isLoading,
  onRefresh,
  maxItemsToShow = 10,
}: ExpiringPartsWidgetProps) {
  const { t } = useTranslation('inventory');
  const [expanded, setExpanded] = useState(true);
  const locale = dateFnsLocale();

  const decorated = useMemo<DecoratedItem[]>(() => {
    if (!items || items.length === 0) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return items
      .map((item) => {
        const expiry = parseISO(`${item.expiryDate}T00:00:00`);
        const daysRemaining = daysBetween(today, expiry);
        const severity: Severity =
          daysRemaining < CRITICAL_THRESHOLD_DAYS ? 'critical' : 'warning';
        return { ...item, daysRemaining, severity };
      })
      .sort((a, b) => a.daysRemaining - b.daysRemaining)
      .slice(0, maxItemsToShow);
  }, [items, maxItemsToShow]);

  const criticalCount = decorated.filter((i) => i.severity === 'critical').length;
  const warningCount = decorated.length - criticalCount;

  const renderHeaderTitle = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <EventBusyIcon color="action" />
      <Typography variant="h6" fontWeight={600}>
        {t('expiring.title')}
      </Typography>
    </Box>
  );

  if (!isLoading && decorated.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {renderHeaderTitle()}
          {onRefresh && (
            <Tooltip title={t('expiring.refresh')}>
              <IconButton size="small" onClick={onRefresh} aria-label={t('expiring.refresh')}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Alert severity="success">{t('expiring.noExpiring')}</Alert>
      </Paper>
    );
  }

  const formatDaysLabel = (days: number): string => {
    if (days < 0) return t('expiring.expired');
    return t('expiring.daysRemaining', { count: days });
  };

  const severityColor = (s: Severity): 'error' | 'warning' =>
    s === 'critical' ? 'error' : 'warning';

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Badge
            badgeContent={decorated.length}
            color={criticalCount > 0 ? 'error' : 'warning'}
          >
            <EventBusyIcon />
          </Badge>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {t('expiring.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {criticalCount > 0 && `${String(criticalCount)} ${t('expiring.critical')}`}
              {criticalCount > 0 && warningCount > 0 && ', '}
              {warningCount > 0 && `${String(warningCount)} ${t('expiring.warning')}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onRefresh && (
            <Tooltip title={t('expiring.refresh')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                aria-label={t('expiring.refresh')}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={expanded ? t('expiring.collapse') : t('expiring.expand')}>
            <IconButton
              size="small"
              aria-label={expanded ? t('expiring.collapse') : t('expiring.expand')}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <List disablePadding>
          {decorated.map((item, index) => (
            <Box key={`${item.partId}-${item.warehouseId}-${item.expiryDate}`}>
              {index > 0 && <Divider />}
              <ListItem sx={{ py: 1.5, px: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ color: `${severityColor(item.severity)}.main` }}>
                    {item.severity === 'critical' ? (
                      <ErrorIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 20 }} />
                    )}
                  </Box>
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.partName}
                      </Typography>
                      <Chip
                        label={item.sku}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('expiring.expiresOn', {
                          date: format(parseISO(`${item.expiryDate}T00:00:00`), 'd.M.yyyy', {
                            locale,
                          }),
                        })}
                      </Typography>
                      <Chip
                        label={formatDaysLabel(item.daysRemaining)}
                        size="small"
                        color={severityColor(item.severity)}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {t('expiring.quantityAt', {
                          qty: item.quantity,
                          warehouse: item.warehouseName || item.warehouseCode,
                        })}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            </Box>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
}
